import { TERMINAL_STATUSES } from "./statusMerge";

/**
 * Canonical section ordering for the workflow detail drawer (consumed by
 * both ora's ``WorkflowDetailDrawer`` and the Outlook plugin's
 * ``InstanceDetailDrawer``).
 *
 * One source of truth so the two surfaces don't drift. The helper returns
 * an ordered list of section keys; each drawer builds a
 * ``Record<DrawerSectionKey, ReactNode | null>`` and iterates this list.
 * Plugin-only or workflow-specific sections (``CarrierHandoff``,
 * ``EndorsementHistory``, cancel controls, retry button) render outside
 * the keyed loop, after the canonical sections.
 */

/**
 * The only two body slots left.
 *
 * This union used to carry eight keys and an ordering function. Everything
 * else -- client, prior decisions, artifacts, run metadata, steps, result --
 * now lives in the run record behind the one more-information entry point,
 * rendered from DATA rather than passed down as pre-built nodes, so there is
 * nothing left to order. These two stay because AC-7 keeps them in the
 * PRIMARY body: the prompt awaiting the Servicer, and what was submitted.
 */
export type DrawerSectionKey = "input_needed" | "request";

export function isTerminalDrawerStatus(status: string): boolean {
  return TERMINAL_STATUSES.has(status);
}

/**
 * Whether the run is still WORKING, as the Steps section means it: it will
 * report more steps, so "no steps yet" is a wait rather than a declaration
 * that the workflow has none.
 *
 * Deliberately narrower than "not terminal". `pending_email_send` and
 * `pending_edocs` are non-terminal but PARKED — waiting on a human or on a
 * carrier, with no further step coming unprompted — and telling the operator
 * we are waiting on the workflow engine would be wrong for both.
 *
 * Shared so the two hosts cannot answer it differently; each used to decide
 * this for itself, and the plugin did not decide it at all.
 */
export function isActivelyWorkingStatus(
  status: string | null | undefined,
): boolean {
  return (
    status === "running" || status === "paused_hitl" || status === "warm_hitl"
  );
}
